/**
 * Clickable booking-code chip. Click to copy the short uppercase code to the
 * clipboard with a "Copied!" toast.
 *
 * Used on vendor / admin / customer booking lists so the support team can quote
 * a booking in one click instead of select-copying the full UUID.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";

/** Derive the short, human-friendly code from a full UUID booking id. */
export function shortBookingCode(id) {
  return String(id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
}

export default function BookingCodeChip({ bookingId }) {
  const [copied, setCopied] = useState(false);
  const code = shortBookingCode(bookingId);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();  // never bubble into card click handlers
    try {
      // Modern Clipboard API requires a secure context (https / localhost).
      // Fallback to the legacy execCommand path so this also works inside
      // older Safari builds + iframes where Clipboard API is blocked.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(`Copied #${code}`);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 font-mono text-[10px] tracking-wider px-2 py-0.5 rounded-md border transition focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
        copied
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-neutral-100 text-neutral-700 border-border hover:bg-neutral-200 hover:text-neutral-900"
      }`}
      title={copied ? "Copied!" : `Click to copy · Full ID: ${bookingId}`}
      data-testid={`booking-code-${bookingId}`}
      data-booking-code={code}
    >
      <span>#{code}</span>
      {copied ? <Check className="h-2.5 w-2.5" /> : <Copy className="h-2.5 w-2.5 opacity-60" />}
    </button>
  );
}
